import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, Clock, Award, GraduationCap, Sparkles, Calendar } from "lucide-react";
import { Reveal, Stagger, Item } from "@/components/Reveal";
import { API, useAuth } from "@/lib/auth";
import { toast } from "sonner";

function formatShortDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  } catch { return iso; }
}

export default function Academy() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nextDates, setNextDates] = useState({});
  const { user, login } = useAuth();
  const [buying, setBuying] = useState(null);

  useEffect(() => {
    axios.get(`${API}/courses`).then((r) => {
      setCourses(r.data);
      setLoading(false);
    });
    axios.get(`${API}/courses-next-dates`).then((r) => setNextDates(r.data || {})).catch(() => {});
  }, []);

  const startCheckout = async (lookupKey) => {
    setBuying(lookupKey);
    try {
      const r = await axios.post(`${API}/payments/checkout`, {
        lookup_key: lookupKey,
        quantity: 1,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setBuying(null);
    }
  };

  return (
    <div data-testid="page-academy" className="pt-32 pb-24">
      <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
        <div className="grid gap-8 lg:grid-cols-12 items-end mb-16">
          <div className="lg:col-span-8">
            <div className="overline">NøoksPro Training Academy</div>
            <h1 className="font-display text-5xl sm:text-7xl mt-4 tracking-tight text-balance">
              Learn the craft, <span className="italic font-light">live the craft.</span>
            </h1>
            <p className="mt-6 max-w-xl text-[hsl(var(--onyx)/0.65)]">
              Accredited courses taught by Malek — 8+ years of salon-floor
              expertise distilled into hands-on training you can actually use.
              Pay in full or spread the cost.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            {user ? (
              <Link
                to="/account"
                data-testid="academy-account-link"
                className="btn-pill btn-outline"
              >
                My courses <ArrowUpRight size={16} />
              </Link>
            ) : (
              <button
                onClick={login}
                data-testid="academy-signin"
                className="btn-pill btn-outline"
              >
                Sign in to purchase
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="py-24 text-center text-[hsl(var(--onyx)/0.5)]">Loading courses…</div>
        ) : (
          <Stagger className="grid gap-8 lg:grid-cols-2">
            {courses.map((c) => (
              <Item key={c.slug}>
                <article
                  data-testid={`course-card-${c.slug}`}
                  className="rounded-[2rem] overflow-hidden bg-white border border-[hsl(var(--onyx)/0.06)] h-full flex flex-col"
                >
                  <div className="hover-zoom aspect-[16/10]">
                    <img src={c.image} alt={c.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-7 sm:p-9 flex-1 flex flex-col">
                    <div className="flex items-center gap-4 text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.5)]">
                      <span className="inline-flex items-center gap-1.5">
                        <Clock size={14} /> {c.duration}
                      </span>
                      {c.accredited && (
                        <span className="inline-flex items-center gap-1.5">
                          <GraduationCap size={14} /> Accredited
                        </span>
                      )}
                    </div>
                    <h2 className="font-display text-3xl sm:text-4xl mt-4 text-[hsl(var(--onyx))]">
                      {c.name}
                    </h2>
                    <p className="mt-3 text-[hsl(var(--onyx)/0.65)] leading-relaxed">
                      {c.tagline}
                    </p>

                    {nextDates[c.slug] && (
                      <div
                        data-testid={`course-next-date-${c.slug}`}
                        className={`mt-5 rounded-2xl px-4 py-3 flex items-center gap-3 border ${
                          nextDates[c.slug].sold_out
                            ? "bg-[hsl(var(--onyx)/0.04)] border-[hsl(var(--onyx)/0.1)]"
                            : "bg-blush/50 border-[hsl(var(--burgundy)/0.15)]"
                        }`}
                      >
                        <div className="w-9 h-9 rounded-xl bg-white/70 flex items-center justify-center shrink-0">
                          <Calendar size={15} className={nextDates[c.slug].sold_out ? "text-[hsl(var(--onyx)/0.5)]" : "text-[hsl(var(--burgundy))]"} />
                        </div>
                        <div className="text-sm flex-1 min-w-0">
                          <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">
                            {nextDates[c.slug].sold_out ? "Next date" : "Next date"}
                          </div>
                          <div className="font-medium text-[hsl(var(--onyx))] leading-tight">
                            {formatShortDate(nextDates[c.slug].date_iso)}
                            {nextDates[c.slug].start_time && ` · ${nextDates[c.slug].start_time}`}
                          </div>
                        </div>
                        {nextDates[c.slug].sold_out ? (
                          <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-[hsl(var(--onyx))] text-[hsl(var(--cream))]">
                            Sold out
                          </span>
                        ) : (
                          <span
                            className={`text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap ${
                              nextDates[c.slug].spaces_remaining <= 2
                                ? "bg-[hsl(var(--burgundy))] text-[hsl(var(--cream))]"
                                : "bg-white text-[hsl(var(--onyx))]"
                            }`}
                          >
                            {nextDates[c.slug].spaces_remaining} space{nextDates[c.slug].spaces_remaining === 1 ? "" : "s"} left
                          </span>
                        )}
                      </div>
                    )}

                    <div className="mt-6 space-y-2">
                      {c.what_you_learn.slice(0, 3).map((w, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm text-[hsl(var(--onyx)/0.7)]">
                          <Sparkles size={14} className="mt-1 text-[hsl(var(--gold))] shrink-0" />
                          {w}
                        </div>
                      ))}
                    </div>

                    <div className="mt-8 pt-6 border-t border-[hsl(var(--onyx)/0.08)]">
                      <div className="flex items-baseline justify-between mb-4">
                        <div>
                          <div className="overline">From</div>
                          <div className="font-display text-3xl text-[hsl(var(--onyx))]">
                            {c.price_from}
                          </div>
                        </div>
                        <Link
                          to={`/academy/${c.slug}`}
                          data-testid={`course-details-${c.slug}`}
                          className="text-sm inline-flex items-center gap-1 text-[hsl(var(--onyx)/0.7)] hover:text-[hsl(var(--onyx))]"
                        >
                          Details <ArrowUpRight size={14} />
                        </Link>
                      </div>

                      <div className="flex flex-col gap-2">
                        {c.lookup_keys.map((lk) => (
                          <button
                            key={lk.lookup_key}
                            onClick={() => startCheckout(lk.lookup_key)}
                            disabled={buying === lk.lookup_key}
                            data-testid={`buy-${lk.lookup_key}`}
                            className="w-full btn-pill btn-primary justify-between disabled:opacity-60"
                          >
                            <span className="flex items-center gap-2">
                              {lk.label}
                              {lk.note && (
                                <span className="text-[10px] uppercase tracking-widest opacity-60">
                                  · {lk.note}
                                </span>
                              )}
                            </span>
                            <span className="flex items-center gap-1">
                              £{lk.amount}
                              <ArrowUpRight size={14} />
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </article>
              </Item>
            ))}
          </Stagger>
        )}

        <Reveal className="mt-24 rounded-[2rem] p-10 sm:p-14 bg-blush-soft grid gap-8 lg:grid-cols-2 items-center">
          <div>
            <Award size={22} className="text-[hsl(var(--gold))]" />
            <h3 className="font-display text-3xl sm:text-4xl mt-3 tracking-tight">
              Certified. Practical. Career-ready.
            </h3>
          </div>
          <p className="text-[hsl(var(--onyx)/0.7)] leading-relaxed">
            Every course is small-group, one-to-one focused, and built around
            real salon scenarios — not theoretical exercises. You leave
            equipped, confident and with a certificate to prove it.
          </p>
        </Reveal>
      </div>
    </div>
  );
}
